import React from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import TopNav from "@/components/TopNav";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Categories from "@/pages/Categories";
import QuestionPage from "@/pages/QuestionPage";
import AuthCallback from "@/pages/AuthCallback";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AdminDashboard from "@/pages/AdminDashboard";
import ProfilePage from "@/pages/ProfilePage";
import AboutPage from "@/pages/AboutPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import ProfileSetupDialog from "@/components/ProfileSetupDialog";
import { useAuth } from "@/context/AuthContext";

function ProfileGate() {
  const { user, status, needsProfile, setNeedsProfile, refresh } = useAuth();
  
  const handleSaved = async () => {
    await refresh();
    setNeedsProfile(false);
  };
  
  return (
    <ProfileSetupDialog
      open={status === "authed" && needsProfile}
      onOpenChange={(v) => {
        // Allow closing only after saving
        setNeedsProfile(v);
      }}
      user={user}
      onSaved={handleSaved}
    />
  );
}

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: handle session_id callback synchronously before any protected checks
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/q/:slug" element={<QuestionPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/profile" element={<ProfilePage />} />
      {/* 404 - Must be last */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
          <AuthProvider>
            <BrowserRouter>
              <TopNav />
              <AppRoutes />
              <ProfileGate />
              <footer className="mx-auto max-w-5xl border-t border-slate-800 px-4 py-8 text-sm text-slate-500">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>© {new Date().getFullYear()} AskIncog • Bangalore's Anonymous Community</div>
                  <div className="flex items-center gap-4">
                    <a data-testid="footer-about-link" className="hover:text-slate-300" href="/about">
                      About
                    </a>
                    <a data-testid="footer-terms-link" className="hover:text-slate-300" href="/terms">
                      Terms
                    </a>
                    <a data-testid="footer-privacy-link" className="hover:text-slate-300" href="/privacy">
                      Privacy
                    </a>
                    <a data-testid="footer-contact-link" className="hover:text-slate-300" href="mailto:hello@askincog.com">
                      Contact
                    </a>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  Safe space for real questions • No hate speech, harassment, or bullying allowed • 
                  <a href="/about" className="hover:text-slate-400 ml-1">Learn more about us</a>
                </div>
              </footer>
            </BrowserRouter>
          </AuthProvider>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}
